<template>
    <div
        class="content-video-header"
        :class="[
            { 'banner-enabled': !!variant },
            variant ? `text-theme-${variant.textTheme ?? 'light'}` : ''
        ]"
    >
        <!-- Background Media -->
        <template v-if="variant">
            <!-- Video -->
            <video
                v-if="variant.type === 'video'"
                :key="variant.url"
                class="fill-width fill-height"
                autoplay
                muted
                loop
                playsinline
                :poster="variant.poster ?? undefined"
            >
                <source
                    :src="variant.url"
                    :type="variant.mime"
                >
                <track kind="captions">
            </video>

            <!-- Image -->
            <v-img
                v-else
                class="fill-width fill-height"
                :src="variant.url"
                :alt="banner?.alt ?? ''"
                cover
            ></v-img>
        </template>

        <!-- Header Content -->
        <div class="slot-header max-container mx-auto px-4 px-md-6 pt-4">
            <slot />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { useIsMobile } from '~/composables';
    import type { Banner, BannerVariant } from '~/types';

    const props = defineProps({
        banner: {
            type: Object as PropType<Nilable<Banner>>,
            default: null
        }
    });

    const { isMobile } = useIsMobile();
    const variant = computed<Nilish<BannerVariant>>(() => {
        if (!props.banner) {
            return null;
        }

        const { desktop, mobile } = props.banner;
        return (isMobile.value ? mobile : desktop) ?? desktop ?? mobile ?? null;
    });
</script>

<style lang="scss">
.content-video-header {
    transition: all 0.5s ease-in-out;

    // Title is always constrained to the page max width with the same padding,
    // whether or not a banner is present (layout via the utility classes in the
    // template; the media stays full-bleed behind it).
    .slot-header .page-title {
        margin-top: -0.4rem;
    }

    &.banner-enabled {
        position: relative;
        padding-bottom: 22vh;
        overflow: hidden;

        video,
        .v-img {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            margin: auto;
            width: 100%;
            height: 100%;
            object-position: center center;
            object-fit: cover;
        }

        // Title overlays the full-bleed media
        .slot-header {
            position: relative;
            filter: opacity(0.9);
        }

        .video-toggle {
            filter: invert(1);
            position: absolute;
            bottom: 0;
            right: 0;
            z-index: 1;
        }
    }

    // Light text on a dark asset — black fallback
    &.text-theme-light {
        background-color: #000;

        .slot-header {
            color: #fff;

            h1 {
                filter: drop-shadow(2px 2px 2px rgba(0, 0, 0, 0.5));
            }
        }
    }

    // Dark text on a light asset — light fallback
    &.text-theme-dark {
        background-color: #fff;

        .slot-header {
            color: #000;

            h1 {
                filter: drop-shadow(2px 2px 2px rgba(255, 255, 255, 0.5));
            }
        }
    }
}
</style>
